import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { InstitutionalNavbar } from '@/components/institutional/InstitutionalNavbar';
import { InstitutionalFooter } from '@/components/institutional/InstitutionalFooter';
import { InfrastructureTrust } from '@/components/institutional/InfrastructureTrust';
import { NotFound } from '../pages/NotFound';
import { PageSEO } from '@/components/PageSEO';
import { InsightsMarkdown } from '@/components/institutional/InsightsMarkdown';
import {
  insights,
  getInsightBySlug,
  getCategoryLabel,
  getAudienceLabel,
  insightsDisclaimer,
} from '@/content/insights';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';

/**
 * Public /insights/:slug article page — Phase 100A.
 *
 * Long-form editorial layout. Renders:
 *   - back link
 *   - category + audience pills
 *   - date + read time + author byline
 *   - title + dek
 *   - article body (markdown, via InsightsMarkdown safe renderer)
 *   - shared disclaimer (gold-bordered)
 *   - CTA block linking to /contact, /for-borrowers, /for-lenders, /insights
 *
 * If `slug` is unknown, redirects to /insights.
 */

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Pill = ({ children, tone = 'gold', testId }) => {
  const palette =
    tone === 'gold'
      ? { bg: 'rgba(176,144,78,0.10)', fg: 'var(--gpc-ink-soft)', ring: 'rgba(176,144,78,0.30)' }
      : { bg: 'rgba(19,35,63,0.05)', fg: 'var(--gpc-ink-soft)', ring: 'rgba(19,35,63,0.12)' };
  return (
    <span
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '0.6875rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        padding: '0.3rem 0.65rem',
        borderRadius: '999px',
        background: palette.bg,
        color: palette.fg,
        boxShadow: `inset 0 0 0 1px ${palette.ring}`,
      }}
    >
      {children}
    </span>
  );
};

const CTA = ({ to, label, primary = false }) => (
  <Link
    to={to}
    data-testid={`insight-cta-${to.replace(/[^a-z]/gi, '-').toLowerCase()}`}
    className={primary ? 'gpc-cta-primary' : 'gpc-cta-secondary'}
    style={{
      height: '2.5rem',
      padding: '0 1.15rem',
      fontSize: '0.875rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.4rem',
    }}
  >
    {label}
    <ArrowUpRight className="w-3.5 h-3.5" />
  </Link>
);

export const InsightArticle = () => {
  const { slug } = useParams();
  const article = getInsightBySlug(slug);

  if (!article) return <NotFound />;

  return (
    <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }}>
      <PageSEO
        title={article.seoTitle || `${article.title} — GP Capital`}
        description={article.seoDescription || article.dek}
        path={`/insights/${article.slug}`} type="article"
      />
      <script type="application/ld+json">{JSON.stringify({'@context':'https://schema.org','@type':'Article',headline:article.title,description:article.dek,datePublished:article.date,author:{'@type':'Organization',name:article.author || 'GP Capital'},publisher:{'@type':'Organization',name:'GP Capital',url:'https://gp-cap.com'},mainEntityOfPage:`https://gp-cap.com/insights/${article.slug}`})}</script><InstitutionalNavbar />

      <article className="pt-20" data-testid="insight-article-page">
        {/* Article hero */}
        <section
          style={{
            background: 'var(--gpc-paper)',
            borderBottom: '1px solid var(--gpc-line-soft)',
          }}
        >
          <div
            className="max-w-3xl mx-auto"
            style={{ padding: 'clamp(2rem, 5vw, 3.5rem) clamp(1.5rem, 4vw, 4rem)' }}
          >
            {/* Back link */}
            <Link
              to="/insights"
              data-testid="insight-back-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '0.8125rem',
                color: 'var(--gpc-ink-soft)',
                textDecoration: 'none',
                marginBottom: '1.5rem',
                transition: 'color 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gpc-ink)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--gpc-ink-soft)'; }}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              All insights
            </Link>

            {/* Meta pills */}
            <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: '1rem' }}>
              <Pill tone="gold" testId="insight-category-pill">
                {getCategoryLabel(article.category)}
              </Pill>
              <Pill tone="muted" testId="insight-audience-pill">
                {getAudienceLabel(article.audience)}
              </Pill>
            </div>

            {/* Title */}
            <h1
              style={{
                fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
                fontWeight: 400,
                fontSize: 'clamp(2rem, 3.8vw, 3rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.015em',
                color: 'var(--gpc-ink)',
                marginBottom: '0.85rem',
              }}
              data-testid="insight-title"
            >
              {article.title}
            </h1>

            {/* Dek */}
            <p
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '1.0625rem',
                color: 'var(--gpc-ink-soft)',
                lineHeight: 1.6,
                marginBottom: '1.5rem',
              }}
              data-testid="insight-dek"
            >
              {article.dek}
            </p>

            {/* Byline + meta */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.65rem',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '0.8125rem',
                color: 'var(--gpc-ink-soft)',
                borderTop: '1px solid var(--gpc-line-soft)',
                paddingTop: '1rem',
              }}
              data-testid="insight-byline"
            >
              <span style={{ color: 'var(--gpc-ink-soft)', fontWeight: 500 }}>{article.author}</span>
              <span>·</span>
              <span>{formatDate(article.date)}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </section>

        {/* Body */}
        <section style={{ background: 'var(--gpc-paper)' }}>
          <div
            className="max-w-3xl mx-auto"
            style={{ padding: 'clamp(2rem, 5vw, 3.5rem) clamp(1.5rem, 4vw, 4rem)' }}
          >
            <InsightsMarkdown source={article.body} testId="insight-body" />
          </div>
        </section>

        {/* Disclaimer */}
        <section style={{ background: 'var(--gpc-paper)' }}>
          <div
            className="max-w-3xl mx-auto"
            style={{ padding: '0 clamp(1.5rem, 4vw, 4rem) clamp(2rem, 4vw, 3rem)' }}
          >
            <div
              data-testid="insight-disclaimer"
              style={{
                borderLeft: '2px solid var(--gpc-gold)',
                paddingLeft: '1rem',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '0.8125rem',
                color: 'var(--gpc-ink-soft)',
                lineHeight: 1.6,
                fontStyle: 'italic',
              }}
            >
              {insightsDisclaimer}
            </div>
          </div>
        </section>

        {/* CTA block */}
        <section
          style={{
            background: 'var(--gpc-panel)',
            borderTop: '1px solid var(--gpc-line-soft)',
            borderBottom: '1px solid var(--gpc-line-soft)',
          }}
          data-testid="insight-cta-block"
        >
          <div
            className="max-w-3xl mx-auto"
            style={{ padding: 'clamp(2.5rem, 5vw, 3.5rem) clamp(1.5rem, 4vw, 4rem)' }}
          >
            <p className="gpc-eyebrow" style={{ marginBottom: '10px' }}>
              For transaction enquiries
            </p>
            <h3
              style={{
                fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
                fontWeight: 400,
                fontSize: 'clamp(1.5rem, 2.6vw, 2rem)',
                lineHeight: 1.2,
                color: 'var(--gpc-ink)',
                marginBottom: '1.5rem',
                letterSpacing: '-0.01em',
              }}
            >
              Contact{' '}
              <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>GP Capital</span>.
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              <CTA to="/contact" label="Contact" primary />
              <CTA to="/for-borrowers" label="For Borrowers" />
              <CTA to="/for-lenders" label="For Capital Partners" />
              <CTA to="/insights" label="All Insights" />
            </div>
          </div>
        </section>
      </article>

      <section className="gpc-container gpc-section"><h2 className="gpc-h3 mb-6">Related Insights</h2><ul className="space-y-4">{insights.filter(a=>a.slug!==article.slug && a.category===article.category).slice(0,3).map(a=><li key={a.slug}><Link style={{color:'var(--gpc-ink-soft)',textDecoration:'underline'}} to={`/insights/${a.slug}`}>{a.title}</Link></li>)}</ul></section>
      <InfrastructureTrust />
      <InstitutionalFooter />
    </div>
  );
};

export default InsightArticle;
